import React, { useState, useEffect, useRef, useContext, useCallback, useMemo } from 'react';
import { UserProfile, Briefcase, DocumentEdit } from '@sravni/react-icons';
import { useDispatch } from 'react-redux';
import dynamic from 'next/dynamic';
import { ValidationError } from 'yup';
import { FormFallbackLoader, Paper } from '../../../../../landing.common';
import { StepWrapper } from '../step.wrapper';
import { useAutoFill } from '../../../../../form/hooks/autofill';
import { useProfile } from '../../../../../form/hooks/profile';
import { FormProgress } from '../form.progress';
import { FORM_FIELDS } from '../../../../../../constants';
import { EventType } from '../../../../../../helpers/analytics';
import { scrollTo } from '../../../../../../helpers';
import { extendFormStore } from '../../../../../../store/reducers/formStore';
import { useLocal } from '../../../../../form/hooks/local';
import { useQueryData } from '../../../../../form/hooks/queryData';
import { useMainSkeleton } from '../../../../../../store/reducers/selection';
import { CardsContext } from '../../../../context';
import { useRouteLoader } from '../../../../../form/hooks/routeLoader';
import { useAddressNormalizer } from '../../../../../form/hooks/addressLoader';
import { useSessionStorageData } from '../../../../../form/hooks/sessionData';
import { OffersModule } from '../../../../../../helpers/dynamic.modules';
import { useEsiaCheckUnsigned } from '../../../../../form/hooks/esiaCheckUnsigned';
import { UnsignedBanner } from '../../../../../gosuslugi';
import { useEsiaUnsignedDocs } from '../../../../../../store/reducers/esia';
import { useAnalytics } from '../../../../../hooks/analytics';
import styles from './styles.module.scss';
import { Step4 } from './components/step4';
import { Step3 } from './components/step3';
import { Step2 } from './components/step2';
import { Step1 } from './components/step1';

const OffersNew = dynamic(OffersModule, {
    ssr: false,
    loading: () => <FormFallbackLoader />,
});

const FORM_STEPS = {
    FIRST_STEP: 1,
    LAST_STEP: 4,
};

export type FormProps = {
    setShowLanding: (v: boolean) => void;
    showLanding: boolean;
};

export const FormSteps: React.FC<FormProps> = ({ showLanding, setShowLanding }) => {
    const { pushEvent } = useAnalytics();
    const { mainSkeletonIsActive } = useMainSkeleton();
    const dispatch = useDispatch();
    const { taskManager } = useContext(CardsContext);

    const stepRefs = useRef<Array<HTMLDivElement | null>>(Array(FORM_STEPS.LAST_STEP).fill(null));

    const prevShowLanding = useRef(showLanding);

    const [step, setStep] = useState(0);
    const stepForProfile = useMemo(() => step + (showLanding ? 0 : 1), [step, showLanding]);
    const unsignedDocs = useEsiaUnsignedDocs();

    useAutoFill(true);
    useLocal(true);
    useQueryData(true);
    useSessionStorageData(true);
    // На странице подбора кредитных карт не сохраняем сумму, цель и период кредита в profile, чтобы они не подставлялись в подборе
    useRouteLoader(taskManager);
    useAddressNormalizer(taskManager);
    useProfile(true, stepForProfile, [FORM_FIELDS.AMOUNT, FORM_FIELDS.PURPOSE, FORM_FIELDS.PERIOD]);
    useEsiaCheckUnsigned();

    useEffect(() => {
        /*
          При первом рендере устанавливаем шаг 1,
          чтобы данные уже были загружены в анкету до запуска валидации
         */
        setStep(1);
    }, []);

    const onNext = useCallback(() => {
        const safeStep = Math.min(step + 1, FORM_STEPS.LAST_STEP);
        setStep(safeStep);
    }, [step]);

    const onGoToStep = useCallback(
        (step: number) => {
            const safeStep = Math.max(Math.min(step, FORM_STEPS.LAST_STEP), FORM_STEPS.FIRST_STEP);
            pushEvent({
                event: EventType.mainEvent,
                eventAction: `Шаг ${safeStep}|Параметры|Шаг Назад`,
            });
            setStep(safeStep);
        },
        [pushEvent],
    );

    useEffect(() => {
        if (!mainSkeletonIsActive) {
            scrollTo(stepRefs.current[step - 1]);
        }
    }, [mainSkeletonIsActive, step]);

    useEffect(() => {
        if (showLanding && !prevShowLanding.current) {
            scrollTo(stepRefs.current[step - 1]);
        }
        prevShowLanding.current = showLanding;
    }, [showLanding]);

    const stopAutoRun = useCallback(() => {
        dispatch(extendFormStore({ autoRun: false }));
    }, [dispatch]);

    const onManualGoToStep = useCallback(
        (step: number) => {
            stopAutoRun();
            onGoToStep(step);
        },
        [stopAutoRun, onGoToStep],
    );

    const onFinalStep = useCallback(() => {
        setShowLanding(false);
        setTimeout(stopAutoRun);
    }, [setShowLanding, stopAutoRun]);

    const onGoBack = useCallback(
        (step?: number) => {
            setShowLanding(true);
            if (typeof step === 'number') {
                const safeStep = Math.max(Math.min(step, FORM_STEPS.LAST_STEP), FORM_STEPS.FIRST_STEP);
                setStep(safeStep);
            }
        },
        [setShowLanding],
    );

    const onAutoValidationError = useCallback(
        (e: ValidationError) => {
            const analyticStepText = `Шаг ${step}`;
            pushEvent({
                event: EventType.mainEvent,
                eventAction: 'Невалидная анкета',
                eventLabel: `${analyticStepText}|${e.path}|Автопроход|${e.value[e.path || ''] || ''}`,
            });
            dispatch(extendFormStore({ autoRun: false }));
        },
        [pushEvent, step, dispatch],
    );

    if (!showLanding) {
        if (unsignedDocs.length > 0) {
            return <UnsignedBanner />;
        }

        return (
            <OffersNew
                onGoBack={onGoBack}
                formStepsConfig={{
                    firstStep: { label: 'Параметры', value: FORM_STEPS.FIRST_STEP },
                    lastStep: { label: 'Место работы', value: FORM_STEPS.LAST_STEP - 1 },
                }}
            />
        );
    }

    return (
        <div className={styles.root}>
            <Paper className={styles.paper}>
                <StepWrapper
                    ref={(step) => (stepRefs.current[0] = step)}
                    title="Шаг 1"
                    subTitle="Контакты"
                    opened={step === 1}
                    completed={step > 1}
                    onClickOpen={() => onManualGoToStep(1)}>
                    <Step1 onNext={onNext} onAutoValidationError={onAutoValidationError} />
                </StepWrapper>
                <StepWrapper
                    ref={(step) => (stepRefs.current[1] = step)}
                    title="Шаг 2"
                    subTitle="Паспортные данные"
                    opened={step === 2}
                    completed={step > 2}
                    onClickOpen={() => onManualGoToStep(2)}
                    imgIcon={<UserProfile />}>
                    <Step2 onNext={onNext} onAutoValidationError={onAutoValidationError} />
                </StepWrapper>
                <StepWrapper
                    ref={(step) => (stepRefs.current[2] = step)}
                    title="Шаг 3"
                    subTitle="Место работы"
                    opened={step === 3}
                    completed={step > 3}
                    onClickOpen={() => onManualGoToStep(3)}
                    imgIcon={<Briefcase />}>
                    <Step3 onNext={onNext} onAutoValidationError={onAutoValidationError} />
                </StepWrapper>
                <StepWrapper
                    ref={(step) => (stepRefs.current[3] = step)}
                    title="Шаг 4"
                    subTitle="Дополнительно"
                    opened={step === 4}
                    imgIcon={<DocumentEdit />}>
                    <Step4 onNext={onFinalStep} onAutoValidationError={onAutoValidationError} />
                </StepWrapper>
            </Paper>
            <FormProgress step={step} />
        </div>
    );
};
